import {
  Loader2,
  AlertCircle,
  Users,
  Building2,
  Briefcase,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  Calendar,
  CreditCard,
  ImageIcon,
  MapPin,
  Shield,
  Banknote,
  Download,
  Printer,
} from 'lucide-react';
import { useEmployee } from '../../../services/httpServices/queries';
import { HrModal } from './HrModal';

interface EmployeeDetailsModalProps {
  open: boolean;
  employeeId: string | null;
  onClose: () => void;
  /** Navigate to the full employee profile page. */
  onViewFull?: (employeeId: string) => void;
  /** Navigate to the employee edit page. */
  onEdit?: (employeeId: string) => void;
  /** Payroll record to act on; enables the "Make Paid" action when not already paid. */
  payroll?: { id: string; status?: string } | null;
  /** Mark the provided payroll record as paid. */
  onMarkPaid?: (record: { id: string; status?: string }) => void;
  /** Whether the mark-paid mutation is in flight. */
  markPaidPending?: boolean;
  /** Download the payslip PDF for the provided payroll record. */
  onDownloadPayslip?: (record: { id: string; status?: string }) => void;
  /** Open the payslip PDF in the browser print dialog. */
  onPrintPayslip?: (record: { id: string; status?: string }) => void;
  /** Whether the payslip download is in flight. */
  payslipPending?: boolean;
  /** Whether the payslip print is in flight. */
  printPending?: boolean;
}

function InfoCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
        <Icon size={16} className="text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-text-muted">{label}</p>
        <p className="text-sm font-medium text-text truncate">{value}</p>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-text-muted">{label}</span>
      <span className="text-sm font-medium text-text text-right">{value}</span>
    </div>
  );
}

/**
 * Employee details shown in a modal. Mirrors the layout of the full
 * `EmployeeView` page so the same information is available from the HR tables
 * without leaving the page.
 */
export function EmployeeDetailsModal({
  open,
  employeeId,
  onClose,
  onViewFull,
  onEdit,
  payroll,
  onMarkPaid,
  markPaidPending,
  onDownloadPayslip,
  onPrintPayslip,
  payslipPending,
  printPending,
}: EmployeeDetailsModalProps) {
  const { data, isLoading, error } = useEmployee(open && employeeId ? employeeId : '');

  const employee = data?.data;
  const fullName = employee
    ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.name
    : '';
  const allowanceTotal = (employee?.allowanceItems || []).reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );
  const mainSalary = employee
    ? (Number(employee.basicSalary) || 0) + allowanceTotal - (Number(employee.taxDeduction) || 0)
    : 0;

  return (
    <HrModal
      open={open}
      onClose={onClose}
      title={fullName || 'Employee Details'}
      subtitle={employee?.designation?.name}
      icon={Users}
      maxWidth="max-w-3xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Close
          </button>
          {employee && onViewFull && (
            <button
              type="button"
              onClick={() => onViewFull(employee.id)}
              className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium"
            >
              Full Profile
            </button>
          )}
          {payroll && onPrintPayslip && (
            <button
              type="button"
              onClick={() => onPrintPayslip(payroll)}
              disabled={printPending}
              className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {printPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Printer size={16} />
              )}
              Print Payslip
            </button>
          )}
          {payroll && onDownloadPayslip && (
            <button
              type="button"
              onClick={() => onDownloadPayslip(payroll)}
              disabled={payslipPending}
              className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {payslipPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              Download Payslip
            </button>
          )}
          {payroll && payroll.status !== 'PAID' && onMarkPaid && (
            <button
              type="button"
              onClick={() => onMarkPaid(payroll)}
              disabled={markPaidPending}
              className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/90 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {markPaidPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Banknote size={16} />
              )}
              Make Paid
            </button>
          )}
          {employee && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(employee.id)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
            >
              Edit Employee
            </button>
          )}
        </>
      }
    >
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {!isLoading && (error || !employee) && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load employee details. Please try again.</span>
        </div>
      )}

      {!isLoading && employee && (
        <div className="space-y-5">
          {/* Profile header */}
          <div className="flex items-center gap-4">
            {employee.photoUrl ? (
              <img
                src={employee.photoUrl}
                alt={fullName}
                className="w-20 h-20 rounded-xl object-cover border border-border shrink-0"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-primary/10 border border-border flex items-center justify-center shrink-0">
                <ImageIcon size={28} className="text-primary/40" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-text truncate">{fullName}</h3>
              <p className="text-sm text-text-muted truncate">
                {employee.designation?.name || 'N/A'}
              </p>
              <span
                className={`inline-flex items-center gap-1 mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  employee.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                }`}
              >
                {employee.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {employee.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Key info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <InfoCard icon={Users} label="Employee Code" value={<span className="font-mono">{employee.employeeCode || 'N/A'}</span>} />
            <InfoCard icon={Building2} label="Department" value={employee.department?.name || 'N/A'} />
            <InfoCard icon={Briefcase} label="Designation" value={employee.designation?.name || 'N/A'} />
            <InfoCard icon={Phone} label="Phone" value={employee.mobileNumber || 'N/A'} />
            <InfoCard icon={Mail} label="Email" value={employee.email || 'N/A'} />
            <InfoCard
              icon={Calendar}
              label="Joining Date"
              value={employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString() : 'N/A'}
            />
          </div>

          {/* Personal + Bank */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-surface border border-border rounded-xl p-4 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <Users size={16} className="text-primary" />
                <h4 className="text-sm font-semibold text-text">Personal Details</h4>
              </div>
              <DetailRow label="Gender" value={<span className="capitalize">{employee.gender || 'N/A'}</span>} />
              <DetailRow
                label="Date of Birth"
                value={employee.dateOfBirth ? new Date(employee.dateOfBirth).toLocaleDateString() : 'N/A'}
              />
              <DetailRow label="Employment Type" value={employee.employmentType?.replace('_', ' ') || 'N/A'} />
              <DetailRow
                label="Gross Salary"
                value={employee.basicSalary ? `৳ ${Number(employee.basicSalary).toLocaleString()}` : 'N/A'}
              />
              <DetailRow label="Allowances" value={`৳ ${allowanceTotal.toLocaleString()}`} />
              <DetailRow label="Tax" value={`৳ ${Number(employee.taxDeduction || 0).toLocaleString()}`} />
              <DetailRow label="Main Salary" value={<span className="text-success">৳ {mainSalary.toLocaleString()}</span>} />
              {employee.address && (
                <div className="flex items-start gap-2 text-sm text-text pt-2">
                  <MapPin size={14} className="text-text-muted shrink-0 mt-0.5" />
                  <span>{employee.address}</span>
                </div>
              )}
            </div>

            <div className="bg-surface border border-border rounded-xl p-4 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard size={16} className="text-primary" />
                <h4 className="text-sm font-semibold text-text">Bank & Documents</h4>
              </div>
              <DetailRow label="Bank Name" value={employee.bankName || 'N/A'} />
              <DetailRow
                label="Account Number"
                value={<span className="font-mono">{employee.bankAccountNumber || 'N/A'}</span>}
              />
              <DetailRow label="Revenue Share Eligible" value={employee.revenueShareEligible ? 'Yes' : 'No'} />
              <DetailRow
                label="Assigned Roles"
                value={
                  employee.user?.roles?.length ? (
                    <span className="flex flex-wrap gap-1 justify-end">
                      {employee.user.roles.map((role: string) => (
                        <span key={role} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">
                          {role.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-text-muted">
                      <Shield size={12} /> No roles
                    </span>
                  )
                }
              />
              <div className="grid grid-cols-2 gap-3 pt-2">
                {employee.nationalIdFrontUrl && (
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">NID Front</p>
                    <img src={employee.nationalIdFrontUrl} alt="NID Front" className="w-full h-24 object-cover rounded-lg border border-border" />
                  </div>
                )}
                {employee.nationalIdBackUrl && (
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">NID Back</p>
                    <img src={employee.nationalIdBackUrl} alt="NID Back" className="w-full h-24 object-cover rounded-lg border border-border" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </HrModal>
  );
}
